// ═══════════════════════════════════════════════════════════════════════
// 数值平衡 —— 全部价格、奖励、乘数、解锁门槛的**单一真相源**
// ═══════════════════════════════════════════════════════════════════════
//
// 为什么要有这个文件：这些数字原本散在 app.js 的七八个位置（价目表在 3079、
// 浇水费在 5498、签到奖励在 3968、品质乘数在 4354……）。同一件事有多处真相源
// 是这个项目已经吃过亏的老毛病（v159 的「按钮红线」排查就是同一个病根），
// 而经济数值比 UI 更危险——**它失衡时不会报错，只会安静地让玩家觉得没意思**。
//
// 这里同时是 tools/test-economy.js 的输入：改任何一个数都要跑一遍它，
// 看种地收入占比、沉没耗尽天数、各作物收益/天有没有被推歪。
//
// ⚠️ 这是浏览器全局，构建时**绝不能开 minifyIdentifiers**（同 crops.js 的 CROPS）。

const BALANCE = {
  // ── 地块与设施 ───────────────────────────────────────────────
  NUM_PLOTS: 6,
  GH_ID: 6,                                 // 温室是第 7 块特殊地
  PLOT_PRICES: { 3: 40, 4: 100, 5: 200 },   // 第 4~6 块地的解锁价
  GH_PRICE: 400,
  START_COINS: 15,                          // 安家费：现实里水要钱，浇水是付费动作

  // ── 日常收支 ─────────────────────────────────────────────────
  //
  // 🔴 这一组是 2026-08-16 经济改造的核心：**出勤不再发钱**。
  // 改造前每天签到 2 + 三条任务 15 + 满勤 6 + 两次巡园 6 ≈ 29🪙，与园子里
  // 种了什么完全无关；而一株满配番茄 85 天也只有 402🪙 ≈ 4.7🪙/天。
  // 也就是说吃透全部系统的玩家，园子贡献的收入不到日常出勤的七分之一——
  // 熟后窗口、经霜转糖、留种传代、地力轮作这些设计的赌注全被稀释成零。
  //
  // 现在出勤只发经验与实物（种子/肥料/节气印），金币水龙头收回到收获这一个口子。
  // 回访动机没有减少（经验、印章、实物照样要每天来拿），但"种得好不好"重新变成
  // 钱包上看得见的事。
  WATER_COST: 1,                  // 自来水基准价，实际按作物 waterNeed 折算（见 waterPrice）
  MILK_COST: 3,                   // 给猫倒一碟牛奶
  SIGN_COIN: 0,                   // 签到（改发经验 + 节气印）
  TASK_COIN: 0,                   // 每日任务（改发经验 + 实物）
  ALLDONE_COIN: 0,                // 满勤（改发一袋肥料，见 ALLDONE_FERT）
  CHORE_COIN: 0,                  // 巡园（改发经验；处理事件本身就是在保住收成）
  VISITOR_COIN: 0,                // 园中访客入册
  ALLDONE_FERT: 1,                // 满勤改发：一袋肥料（施下去 +15% 产量，仍然要种得好才兑现）
  RESCUE_SHARE: 0.18,             // 抢救费 = 这一茬身价（cropSeasonValue）的这个比例

  // 浇水按作物需水系数收费：黄瓜（1.3）比胡萝卜（0.9）费水，这是真实的，
  // 也让"选品"多一个经济维度——过去 waterNeed 只进生长模型，从来不进钱包。
  // 向上取整保证最便宜的作物仍要 1🪙（免费的水会让水这件事彻底不算数）。
  waterPrice(crop) {
    return Math.max(1, Math.ceil((crop && crop.waterNeed || 1) * BALANCE.WATER_COST));
  },

  // ── 收获 ─────────────────────────────────────────────────────
  QUALITY_MULT: { prime: 2, fine: 1.5, normal: 1 },
  SEED_GEN_BONUS: 0.05,           // 传家宝每代 +5%
  SEED_GEN_CAP: 5,                // 封顶第 5 代
  FERT_YIELD: 1.15,               // 一袋肥料 +15% 产量
  FERT_SOIL: 30,                  // 施在空地上回地力 +30
  FERT_SOIL_MAX: 85,              // 地力高于此值不许再施（防囤肥刷地力）
  COMMISSION_BASE: 15,            // 苗叔委托酬谢 = 此值 + 这一茬身价的 COMMISSION_SHARE
  COMMISSION_SHARE: 0.35,

  // ── 防护物资 ─────────────────────────────────────────────────
  COVER_STOCK: 3,                 // 平时囤
  COVER_RUSH: 10,                 // 临期抢（3.3 倍）
  WARN_DAYS: 7,                   // 预警窗口
  // ⚠️ 「一张管几天」在 COVER_DAYS_LV（随等级），不在这里。原先并存的固定值 COVER_DAYS: 5
  // 已删——留着只会被将来的人当成真相源用，而它早就不是了。同理见 BARREL_CAPS 下方那条。

  // ── 雨水缸与堆肥 ─────────────────────────────────────────────
  BARREL_MM: 2,                   // 当天下满这么多 mm 接住一桶
  // ⚠️ 容量看 BARREL_CAPS（随等级 3→4→5→6），这里不再放固定值。
  // 原先并存的 BARREL_CAP: 3 已删：它不被任何代码引用，却是一个**看起来权威的错数**——
  // 服务端 merge_saves 就照着 3 把高等级玩家的水截掉过（2026-08-27 修）。
  // ── 蜂箱产蜜 ─────────────────────────────────────────────────
  // 蜜蜂只在晴暖的日子出巢采蜜——判据全是真实天气，一颗骰子不掷。
  // 这是"每周"那一格的另一条节拍：连续采收只属于种了果菜的人，蜂箱属于所有人。
  // 一罐蜜的价钱按"买蜂箱 50🪙、好天气下约两个月回本"定：每天最多约 1🪙，
  // 比任何一畦作物都少——它是添头，不能让装饰比种地更赚钱（金币只从收获来）。
  HONEY_TMAX: 15,                 // 白天最高温到这个数，蜜蜂才出巢
  HONEY_RAIN_MM: 1,               // 下雨天不出巢
  HONEY_SUN_H: 4,                 // 日照不足也不出（缺日照数据时不看这一条）
  HONEY_DAYS: 7,                  // 攒几个采蜜日出一罐
  HONEY_CAP: 3,                   // 蜂箱里最多存几罐——再不取，蜂群就自己吃掉了
  HONEY_JAR_COIN: 6,              // 一罐蜜卖多少（天天好天也只有 0.86🪙/天，低于最便宜的一档作物）
  COMPOST_NEED: 3,                // 几株残料起发酵
  COMPOST_GDD: 60,                // 发酵所需真实积温

  // ── 土壤 ─────────────────────────────────────────────────────
  SOIL_START: 88,
  SOIL_REGEN: 1.5,                // 歇地每天回力
  SOIL_DRAIN: 12,                 // 收一茬掉地力（长周期作物见下）
  SOIL_DRAIN_HEAVY: 18,           // gddMature >= 900 的重茬作物
  SOIL_HEAVY_GDD: 900,
  LEGUME_FIX: 20,                 // 豆科根瘤固氮，反哺土壤

  // ── 等级给的是能力，不是只有新品种 ────────────────────────────
  //
  // 改造前 unlockLv 与经济价值毫无相关性、甚至是反的：Lv.5 解锁的大白菜曾是全表
  // 收益最差的一种，Lv.8 的苦瓜不如 1 级就能种的番茄。等级只是个计数器——玩家
  // 投入三周，得到的是"更多选项"，不是"更有本事"。
  //
  // 现在品种仍按农学难度解锁（那是对的，它是一份种植日历），但等级另外给三样**能力**。
  // 三条共同的设计约束：
  //   ① 只加不减。往上调门槛等于把老玩家已经有的东西收回去，这条线不能碰。
  //   ② 每一样都必须仍然符合"天气数据驱动"——放大的是玩家利用真实天气的能力，
  //      不是凭空发资源（雨水缸更大＝接住更多真实的雨；覆膜更久＝盖得住更长的寒潮）。
  //   ③ 查表函数对任何等级都要有返回值，包括 1 级和 99 级。
  TRAY_SLOTS: [[1, 4], [6, 5], [9, 6]],        // 育苗盘格位：冬天唯一有事可做的那一格
  BARREL_CAPS: [[1, 3], [4, 4], [7, 5], [10, 6]], // 雨水缸容量：接住真实降雨的上限
  COVER_DAYS_LV: [[1, 5], [5, 6], [8, 7]],     // 一张覆膜/遮阳网管几天
  // 种子站雨露包交齐后雨水缸多一桶。⚠️ 服务端 BARREL_WATER_MAX 要把它算进去（test-economy 门禁焊着）
  BUNDLE_BARREL_BONUS: 1,
  perk(table, lv) {
    let v = table[0][1];
    for (const [need, val] of table) if ((lv || 1) >= need) v = val;
    return v;
  },

  // ── 旱情限水 ─────────────────────────────────────────────────
  // 自来水过去只要有钱就无限供应，所以"浇不浇"从来不是决定——钱够就浇，钱不够
  // 也没别的可干。真正经典的决策形态是**资源不够、必须取舍**：六块地只能救两块，
  // 救哪两块？
  //
  // 触发条件完全来自玩家窗外的真实天气（连续多少天没下够雨），一颗骰子都不掷；
  // 限水本身也是现实里真的会发生的市政措施（爱尔兰、英国的 hosepipe ban 每隔
  // 几年就来一次）。这是"真实数据驱动一切"能长出来的机制，不是凭空加的难度。
  DROUGHT_RAIN_MM: 1,             // 一天下不到这个量就算"没下雨"
  DROUGHT_LEVELS: [               // [连续无雨天数, 每天还能买几次自来水]
    [21, 1],
    [14, 2],
  ],
  DROUGHT_TAP_FREE: 99,           // 未触发限水时不设上限

  // ── 金币流水账本 ─────────────────────────────────────────────
  // 账本只作展示/审计，不参与余额裁决，但"能看见最近发生了什么"本身就是信任的
  // 来源——余额对不上时，玩家唯一能自查的就是这张流水。
  // ⚠️ 这个数客户端与服务端**各要剪一次**（两边都可能拿到超长的账本），所以它必须
  // 只有一份真相源：server/app.py 的 COIN_LOG_MAX 由 test-economy.js 焊住。
  COIN_LOG_MAX: 80,

  // ── 经验 ─────────────────────────────────────────────────────
  // 出勤不再发钱之后，经验就是出勤的**唯一**回报，所以整体上调：
  // 玩家每天回来拿到的仍然是一条看得见的进度条，只是它换成了等级而不是钱包。
  XP_REWARD: { water: 3, chore: 6, plant: 6, harvest: 20, pick: 6, sign: 4, task: 8, ach: 20 },
};

// ── 作物售价：档位定"每天值多少"，农学定"要多少天" ─────────────
//
// 旧写法是 crops.js 里一个手写的 `coinValue`（1~5）。它的问题不是数值不准，
// 是**它跟作物押上的时间没有任何关系**：把 yieldBase × coinValue 除以典型天数，
// 36 种可种作物里只有 5 种超过 1🪙/天（而浇一次水正好 1🪙），而长周期高风险的
// 辣椒 0.92、茄子 0.57、南瓜 0.24 反倒不如速生叶菜划算。玩家因此没有任何理由
// 为一株番茄承担 85 天的天气风险。更糟的是它对新作物完全失控——大白菜一畦只结
// 3 棵（yieldBase=3），手写单价怎么填都会掉出所有人的量级。
//
// 现在改成一条规则，不再有手写表：
//
//   一茬的目标进账 = 档位的「每天值多少」 × 这个作物按农学该长多少天
//   单价           = 目标进账 ÷ yieldBase
//
// 于是"番茄比生菜值钱"不再靠人记得填对，而是因为它本来就要长两倍久、还怕霜。
// 加新作物时**什么都不用填**：档位和天数都从它自己的农学参数推出来。
//
// 副作用是同档作物的每天收益趋同——这是想要的：区分它们的应该是天气风险、
// 品质加成与熟后窗口（那些都是真实数据驱动的），而不是一张静态价目表。
// 保留一个 ±25% 的"风味系数"，让作者原本填的 coinValue 仍然说得上话。
const VALUE_TIERS = [
  // perDay = 这一档的作物，每占用你一天现实时间，该进账多少 🪙
  { id: 'quick', perDay: 1.0, zh: '速生', en: 'Quick' },       // 短周期低风险，随时能补种
  { id: 'season', perDay: 1.6, zh: '当季', en: 'Seasonal' },
  { id: 'long', perDay: 2.0, zh: '长茬', en: 'Long-season' },   // 长周期但耐寒，稳
  { id: 'stake', perDay: 2.8, zh: '重注', en: 'High-stakes' },  // 长周期且怕霜：最值得等，也最赔得起
];

function valueTier(crop) {
  const gdd = crop.gddMature || 0;
  // frostDamage 是"开始受害的温度"：数值越高越怕霜（黄瓜 +4 / 菠菜 −8）
  const tender = (crop.frostDamage != null ? crop.frostDamage : -20) >= 0;
  if (crop.indoor) return VALUE_TIERS[0];              // 窗台盆栽不担天气风险，永远第一档
  if (gdd >= 800) return VALUE_TIERS[tender ? 3 : 2];
  if (gdd >= 450) return VALUE_TIERS[tender ? 2 : 1];
  return VALUE_TIERS[0];
}

// 这个作物在自己的适生区间里该长多少天：成熟积温 ÷ 适生均温超出基点的部分。
// 纯农学推导，不读 daysHint（那是给人看的展示串，不该参与结算）。
// 和 tools/test-crops.js 的"隐含均温"是同一条式子的两个方向，中位吻合度 0.92。
function agronomicDays(crop) {
  const mid = ((crop.tMinGrow + crop.tMaxGrow) / 2) - crop.tBase;
  return Math.max(5, crop.gddMature / Math.max(1, mid));
}

// 作物全表从哪儿来：浏览器里 crops.js 的 `const CROPS` 是**全局词法声明**（不是
// window 的属性），只能用裸标识符取；node 里它是模块导出，得 require 进来。
// ⚠️ 不能写 `typeof CROPS !== 'undefined'` 兜底——typeof 挡不住 TDZ，
// 调用方只要在顶层写过 `const CROPS = require(...)`（`node -e` 就是这种场景），
// 这里就会直接抛 ReferenceError 而不是回落。
let _cropSource = null;   // node 端在文件末尾注入；浏览器留空，走全局 CROPS
function allCrops() {
  if (_cropSource) return _cropSource;
  try { return CROPS; } catch (e) { return []; }
}

// 同档内的风味系数：作者填的 coinValue 相对本档中位数的偏离，钳在 ±25%。
// 让"辣椒就是比黄瓜金贵"这类手感留下来，又不让它把量级带跑。
const _tierMedianValue = {};
function tierMedianValue(tierId) {
  if (_tierMedianValue[tierId] == null) {
    const src = allCrops().filter(c => valueTier(c).id === tierId);
    const vs = src.map(c => c.coinValue || 1).sort((a, b) => a - b);
    _tierMedianValue[tierId] = vs.length ? vs[vs.length >> 1] : 1;
  }
  return _tierMedianValue[tierId];
}

// 一茬满健康、普通品质、不等熟后窗口，能卖多少钱。凡是"按这一茬的身价定价"的
// 地方（抢救费、委托酬谢）都从这里取，别再乘 coinValue——那是风味输入不是价格。
function cropSeasonValue(crop) {
  const tier = valueTier(crop);
  const flavor = Math.max(0.75, Math.min(1.25, (crop.coinValue || 1) / tierMedianValue(tier.id)));
  return tier.perDay * agronomicDays(crop) * flavor;
}

function cropUnitValue(crop) {
  // 单价保留两位：低产量作物（大白菜一畦 3 棵）靠单价补回来，取整会把它打回原形
  return Math.max(0.1, +(cropSeasonValue(crop) / Math.max(1, crop.yieldBase || 1)).toFixed(2));
}

// 连续无雨天数（从最后一天往回数）。只认可测量的 mm，与 sim.js 的水分模型同一口径——
// 画面上飘着毛毛雨但土还在干，是物理正确的行为，限水判定也必须跟着这条线走。
function dryStreak(days) {
  let n = 0;
  for (let i = days.length - 1; i >= 0; i--) {
    if ((days[i].precip || 0) >= BALANCE.DROUGHT_RAIN_MM) break;
    n++;
  }
  return n;
}

// 今天的限水档位：{ streak, limit, on }
function droughtState(days) {
  const streak = dryStreak(days || []);
  for (const [need, limit] of BALANCE.DROUGHT_LEVELS) {
    if (streak >= need) return { streak, limit, on: true };
  }
  return { streak, limit: BALANCE.DROUGHT_TAP_FREE, on: false };
}

if (typeof module !== 'undefined') {
  _cropSource = require('./crops.js').CROPS;   // crops.js 不反向依赖本文件，无循环
  module.exports = { BALANCE, VALUE_TIERS, valueTier, cropUnitValue, cropSeasonValue, agronomicDays, dryStreak, droughtState };
}
